import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';
import CreatePerceptionModelProjectTranslation from '@graphql/mutations/CreatePerceptionModelProjectTranslation';
import {
  AllProjectTranslationsQuery,
  type PerceptionModelJobOption,
  type TranslationListData,
} from '@graphql/queries/PerceptionModelQueries';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import AdvancedGrid from './AdvancedGrid';
import {
  DEMO_DIMENSIONS,
  buildDemographic,
  cellCount,
  defaultDraft,
  effectiveLevels,
  isFallbackLevels,
  suggestName,
  type DemoDimension,
  type DimensionDraft,
} from './demographic';
import DimensionEditor from './DimensionEditor';
import { isNameTaken, saveErrorMessage, uniqueName } from './translationNames';

/**
 * Add a translation to a saved norm set.
 *
 * The norm set is the parent, so it is context rather than a field: the model
 * comes from it, and a translation renders against that model however old it
 * is. Products default to the norm set's members but are not limited to them.
 *
 * Mounted only once the model's levels are known, since the opening draft is
 * narrowed to them — remounting to re-seed would discard whatever had already
 * been typed.
 */

type Props = {
  projectId: number;
  job?: PerceptionModelJobOption;
  /** the norm set's own members, the default product list */
  members: string[];
  /** the saved row's id, so the caller can go straight to it */
  onDone: (createdId: number) => void;
  /** leave without saving; omitted when there is nowhere to go back to */
  onCancel?: () => void;
};

const TranslationBuilder: React.FC<Props> = ({
  projectId,
  job,
  members,
  onDone,
  onCancel,
}) => {
  const [name, setName] = React.useState('');
  // Whether the name is the user's own. Until it is, it tracks the picker —
  // a name that stops matching the audience it describes is worse than no
  // default at all, and typing one out by hand is the common case.
  const [named, setNamed] = React.useState(false);

  // What the model recorded, when it recorded anything. Nothing has stamped
  // demo_levels yet, so in practice this is the fixed vocabulary.
  const trainedLevels = job?.params?.demo_levels ?? null;
  const available = React.useMemo(
    () => effectiveLevels(trainedLevels),
    [trainedLevels],
  );
  const fallback = isFallbackLevels(trainedLevels);

  const [draft, setDraft] = React.useState(() => defaultDraft(available));

  // The siblings, because the table is UNIQUE (project, name) and the suggested
  // name is a pure function of the audience — two translations for the same
  // audience on one norm set would otherwise collide every time.
  const { data: siblingData } = useQuery(AllProjectTranslationsQuery, {
    variables: { projectId },
  });
  const taken = React.useMemo(
    () =>
      (siblingData?.allPerceptionModelProjectTranslations?.nodes ?? []).map(
        (t: { name: string }) => t.name,
      ),
    [siblingData],
  );

  const result = React.useMemo(
    () => buildDemographic(draft, trainedLevels),
    [draft, trainedLevels],
  );

  // Suggested names step around a name already in use; a typed one is the
  // analyst's, so it is reported rather than quietly altered.
  const effectiveName = named ? name : uniqueName(suggestName(draft), taken);
  const collides = named && isNameTaken(effectiveName, taken);

  // Any edit makes the name theirs, including clearing it. Handing the field
  // back to the suggestion when it goes empty reads well until someone
  // backspaces through it: the suggestion reappears under the cursor and the
  // next keystroke lands on the end of it.
  const onNameChange = (value: string) => {
    setName(value);
    setNamed(true);
  };

  const cells = cellCount(draft);

  const [create, { loading: saving, error: saveError }] = useMutation(
    CreatePerceptionModelProjectTranslation,
    {
      update: (cache, { data }) => {
        const created =
          data?.createPerceptionModelProjectTranslation
            ?.perceptionModelProjectTranslation;
        if (!created) return;
        const existing = cache.readQuery<TranslationListData>({
          query: AllProjectTranslationsQuery,
          variables: { projectId },
        });
        if (!existing?.allPerceptionModelProjectTranslations) return;
        cache.writeQuery({
          query: AllProjectTranslationsQuery,
          variables: { projectId },
          data: {
            allPerceptionModelProjectTranslations: {
              ...existing.allPerceptionModelProjectTranslations,
              nodes: [
                created,
                ...existing.allPerceptionModelProjectTranslations.nodes,
              ],
            },
          },
        });
      },
      onCompleted: (result) =>
        onDone(
          result?.createPerceptionModelProjectTranslation
            ?.perceptionModelProjectTranslation?.id,
        ),
      // Without this the execute function rejects unhandled; saveError already
      // renders the reason.
      onError: () => {},
    },
  );

  const onSave = () => {
    if (!result.demographic) return;
    create({
      variables: {
        translation: {
          name: effectiveName.trim(),
          perceptionModelProjectId: projectId,
          products: members,
          demographic: result.demographic,
        },
      },
    });
  };

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <TextField
        label="Translation name"
        value={effectiveName}
        onChange={(e) => onNameChange(e.target.value)}
        error={collides || (named && !effectiveName.trim())}
        helperText={
          collides
            ? 'Already used on this norm set — every translation here needs its own name.'
            : named
              ? effectiveName.trim()
                ? 'Your name — it no longer follows the picker.'
                : 'A translation needs a name.'
              : 'Follows the audience below until you change it.'
        }
        fullWidth
      />

      {fallback && (
        <Alert severity="info">
          This model did not record which demographic levels it trained on, so
          the levels below are the fixed vocabulary: age, gender and smoking are
          decided by review cleaning and cannot be anything else, and countries
          are those with more than ten panellists. That catches a level which
          cannot exist, but not one this model simply never saw — which would
          render as the reference level rather than failing. Re-run the export
          for this model to record its real levels.
        </Alert>
      )}

      {DEMO_DIMENSIONS.map((d: DemoDimension) => (
        <DimensionEditor
          key={d}
          dimension={d}
          draft={draft[d]}
          available={available[d]}
          onChange={(next: DimensionDraft) =>
            setDraft((prev) => ({ ...prev, [d]: next }))
          }
        />
      ))}

      <Box>
        <Typography variant="body2">
          {cells} demographic {cells === 1 ? 'cell' : 'cells'} ·{' '}
          {members.length} products
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Every cell carries a weight, and the weights across the whole grid sum
          to 1.
        </Typography>
      </Box>

      <AdvancedGrid demographic={result.demographic} />

      {result.problems.map((problem) => (
        <Alert key={problem} severity="warning">
          {problem}
        </Alert>
      ))}

      {saveError && (
        <Alert severity="error">
          {saveErrorMessage(saveError.message, effectiveName)}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          disabled={
            !result.demographic ||
            !effectiveName.trim() ||
            collides ||
            saving ||
            !members.length
          }
          onClick={onSave}
        >
          {saving ? 'Saving…' : 'Add translation'}
        </Button>
        {onCancel && (
          <Button sx={{ ml: 1 }} disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default TranslationBuilder;
